import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Film, Camera, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';
import useSeo from '../hooks/useSeo';
import { optimizeImageUrl } from '../lib/optimizeImage';

const Services = () => {
  useSeo({
    title: 'Services — Motion, Animation, Video & Photography in DFW',
    description: 'Motion design, character design, video editing, family & real estate photography in Mansfield, TX — serving the Dallas–Fort Worth Metroplex.',
  });

  const iconMap = {
    'Sparkles': <Sparkles size={48} />,
    'Users': <Users size={48} />,
    'Film': <Film size={48} />,
    'Camera': <Camera size={48} />
  };

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Hero */}
      <section className="py-14 lg:py-20 bg-gradient-to-b from-blue-900/20 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="page-title text-4xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-blue-400 to-lime-400 bg-clip-text text-transparent">Services</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive creative services designed to elevate your brand and tell your story with impact.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="text-blue-400 mb-6">{iconMap[service.icon]}</div>
                <h2 className="text-2xl lg:text-4xl font-bold mb-4">{service.title}</h2>
                <p className="text-base sm:text-lg text-gray-300 mb-6">{service.description}</p>

                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-4">What I Offer:</h3>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="text-blue-400 mr-3 flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Card className="bg-gray-900/50 border-gray-800 mb-6">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-3 text-blue-400">Use Cases</h3>
                    <p className="text-gray-300">{service.useCases}</p>
                  </CardContent>
                </Card>

                <Link to="/contact">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3">
                    Let's Talk <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>

              {/* Image */}
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src={optimizeImageUrl(
                      index === 0
                        ? 'https://images.unsplash.com/photo-1676238560626-45d35b63b38f'
                        : index === 1
                        ? 'https://images.unsplash.com/photo-1611643378160-39d6dd915b69'
                        : index === 2
                        ? 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d'
                        : 'https://images.unsplash.com/photo-1532272278764-53cd1fe53f72',
                      1200
                    )}
                    alt={service.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover aspect-video"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-14 lg:py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">My Process</h2>
            <p className="text-lg sm:text-xl text-gray-400">A collaborative approach to bringing your vision to life</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'Understanding your goals, audience, and brand story' },
              { step: '02', title: 'Concept', description: 'Developing creative concepts and visual direction' },
              { step: '03', title: 'Production', description: 'Bringing the vision to life with precision and artistry' },
              { step: '04', title: 'Delivery', description: 'Final delivery with revisions and optimization' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-blue-400/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 lg:py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">Let's create something amazing together</h2>
          <p className="text-lg sm:text-xl text-gray-400 mb-8">
            Ready to get started? Request a quote and let's discuss your project.
          </p>
          <Link to="/contact">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg">
              Request a Quote <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;